import { PayloadAction } from '@reduxjs/toolkit';
import { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import { PageList } from '@/types/list';
import { ProducerEntityType } from '@/domains/producers/types/ProducerEntityType';
import { GetEntityAddressHistoryPayload } from '../store/types/GetEntityAddressHistoryPayload';
import { RefreshContactsListPayload } from '../store/types/RefreshContactsListPayload';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { EdsDefinitionSections } from '@/features/eds/enums/EdsDefinitionSections';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';
import EdsAdapterService from '@/features/eds/services/EdsAdapterService/EdsAdapterService';
import EdsDebugService from '@/features/eds/services/EdsDebugService/EdsDebugService';
import { selectContactListPaginationRequest } from '@/features/entityDetails/store/entityDetailsSelector';
import {
  deleteEntityPaymentInformation,
  getBeneficiaryDetails,
  getContactList,
  getContactListSuccess,
  getEntityAddressHistory,
  getEntityAddressHistoryError,
  getEntityAddressHistorySuccess,
  getEntityDetailsError,
  getEntityDetailsSuccess,
  getInsuredDetails,
  getProducerDetails,
  incrementContactListPage,
  refreshContactList,
  refreshContactListSuccess,
  startLoadingContactList,
  startLoadingEntityDetails,
} from '@/features/entityDetails/store/entityDetailsSlice';
import { getContactListFilterParams } from '@/features/entityDetails/utils/getContactListParams';
import { getFilterPropertyName } from '@/features/entityDetails/utils/getFilterPropertyName';
import { normalizeContactList } from '@/features/entityDetails/utils/normalizeContactList';
import { SnackType } from '@/features/snacks/enums/SnackType';
import PaymentFormEvent from '@/domains/payments/features/paymentModals/shared/events';
import { SortDirection } from '@/enums/SortDirection';
import EventService from '@/services/EventService/EventService';
import HttpService from '@/services/HttpService/HttpService';
import QueryParamsService from '@/services/QueryParamsService/QueryParamsService';
import SnacksService from '@/services/SnackService/SnackService';

const CONTACT_INCLUDES = [
  `${EdsEntityCodes.Party}.${EdsEntityCodes.Person}.${EdsEntityCodes.ContactInformation}`,
  `${EdsEntityCodes.Party}.${EdsEntityCodes.Business}.${EdsEntityCodes.ContactInformation}`,
  `${EdsEntityCodes.Party}.${EdsEntityCodes.Person}.${EdsEntityCodes.Addresses}.${EdsEntityCodes.AddressTypes}.${EdsEntityCodes.AddressType}`,
  `${EdsEntityCodes.Party}.${EdsEntityCodes.Business}.${EdsEntityCodes.Addresses}.${EdsEntityCodes.AddressTypes}.${EdsEntityCodes.AddressType}`,
  `${EdsEntityCodes.Party}.${EdsEntityCodes.PaymentMethods}.${EdsEntityCodes.Address}`,
  `${EdsEntityCodes.HelperTags}`,
  `${EdsEntityCodes.Policy}`,
  `${EdsEntityCodes.Coverage}`,
].join(',');

function* getEDSBeneficiaryDetailsSaga(
  action: PayloadAction<{
    beneficiaryId: string;
  }>,
) {
  try {
    if (!action.payload.beneficiaryId) return;

    yield put(startLoadingEntityDetails());

    const { data } = yield call(
      HttpService.get,
      `v2/beneficiary/${action.payload.beneficiaryId}?includeEntities=Claimant,Party.Person.ContactInformation,Party.Business.ContactInformation,Contacts.Party.Person.ContactInformation,Contacts.Party.Business.ContactInformation,Party.Person.Addresses.AddressTypes.AddressType,Party.Business.Addresses.AddressTypes.AddressType,Party.PaymentMethods.Address,HelperTags`,
      {},
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );

    const normalizedResponse: NormalizedV2Response = yield call(EdsAdapterService.normalize, data, {
      section: EdsDefinitionSections.SidePanel,
      includeId: true,
    });

    const personOrBusinessData =
      ((normalizedResponse.entities[EdsEntityCodes.Party] as NormalizedV2Response).entities[
        EdsEntityCodes.Person
      ] as NormalizedV2Response) ??
      ((normalizedResponse.entities[EdsEntityCodes.Party] as NormalizedV2Response).entities[
        EdsEntityCodes.Business
      ] as NormalizedV2Response);

    const personOrBusinessDataAddressList =
      (personOrBusinessData.entities[EdsEntityCodes.Addresses] as NormalizedV2Response[] | undefined) ??
      (personOrBusinessData.entities[EdsEntityCodes.Addresses] as NormalizedV2Response[] | undefined);

    const entityAddressList =
      personOrBusinessDataAddressList?.map((item) => ({ ...item, addressEditId: item.id }) as NormalizedV2Response) ??
      [];

    if (data) {
      EdsDebugService.logResponse(EdsEntityCodes.Beneficiary, {
        rawResponse: data,
        normalizedResponse,
        includedEntitiesRecord: normalizedResponse.entities,
      });
      yield put(
        getEntityDetailsSuccess({
          entityDetailsData: normalizedResponse,
          personOrBusinessData,
          contactInformationData: personOrBusinessData.entities[
            EdsEntityCodes.ContactInformation
          ] as NormalizedV2Response,
          entityAddressList,
          entityAddressHistory: null,
        }),
      );
    }
  } catch (error) {
    yield put(getEntityDetailsError());
    console.error(error);
  }
}

function* getContactListSaga() {
  try {
    yield put(startLoadingContactList());
    const pagination = yield select(selectContactListPaginationRequest);

    const contactFilters = getContactListFilterParams();

    const { data: contacts } = yield call(
      HttpService.post,
      `/filter/${EdsEntityCodes.Contact}?${QueryParamsService.constructSearchParamsFromRecord({
        ...pagination,
        includes: CONTACT_INCLUDES,
        sortBy: 'CreatedAt',
        sortDirection: SortDirection.DESC,
      })}`,
      contactFilters,
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );

    const normalizedContactList = yield call(normalizeContactList, contacts.listings, {
      section: EdsDefinitionSections.SidePanel,
      includeId: true,
    });

    yield put(getContactListSuccess({ listings: normalizedContactList, pagination: contacts.pagination }));
  } catch (error) {
    console.error(error);
  }
}

function* refreshContactListSaga({ payload }: PayloadAction<RefreshContactsListPayload | undefined>) {
  try {
    yield put(startLoadingContactList());

    const pagination: PageList = yield select(selectContactListPaginationRequest);
    const contactFilters =
      payload?.propertyName && payload?.id
        ? [{ propertyName: payload.propertyName, values: [payload.id] }]
        : getContactListFilterParams();

    const { data: contacts } = yield call(
      HttpService.post,
      `/filter/${EdsEntityCodes.Contact}?${QueryParamsService.constructSearchParamsFromRecord({
        page: 1,
        perPage: pagination.page * pagination.perPage,
        includes: CONTACT_INCLUDES,
        sortBy: 'CreatedAt',
        sortDirection: SortDirection.DESC,
      })}`,
      contactFilters,
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );

    const normalizedContactList = yield call(normalizeContactList, contacts.listings, {
      section: EdsDefinitionSections.SidePanel,
    });

    yield put(refreshContactListSuccess(normalizedContactList));
  } catch (error) {
    console.error(error);
  }
}

function* getEDSInsuredDetailsSaga(
  action: PayloadAction<{
    insuredId: string;
  }>,
) {
  try {
    yield put(startLoadingEntityDetails());

    const { data } = yield call(
      HttpService.get,
      `v2/insured/${action.payload.insuredId}?includeEntities=Party.Person.ContactInformation,Contacts.Party.Person.ContactInformation,Contacts.Party.Business.ContactInformation,Party.Person.Addresses.AddressTypes.AddressType,Party.PaymentMethods.Address,HelperTags`,
      {},
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );

    const normalizedResponse: NormalizedV2Response = yield call(EdsAdapterService.normalize, data, {
      section: EdsDefinitionSections.SidePanel,
      includeId: true,
    });

    const personAddressList = (
      (normalizedResponse.entities[EdsEntityCodes.Party] as NormalizedV2Response).entities[
        EdsEntityCodes.Person
      ] as NormalizedV2Response
    ).entities[EdsEntityCodes.Addresses] as NormalizedV2Response[];

    const entityAddressList =
      personAddressList?.map((item) => ({ ...item, addressEditId: item.id }) as NormalizedV2Response) ?? [];

    if (data) {
      EdsDebugService.logResponse(EdsEntityCodes.Insured, {
        rawResponse: data,
        normalizedResponse,
        includedEntitiesRecord: normalizedResponse.entities,
      });

      yield put(
        getEntityDetailsSuccess({
          entityDetailsData: normalizedResponse,
          personOrBusinessData: (normalizedResponse.entities[EdsEntityCodes.Party] as NormalizedV2Response).entities[
            EdsEntityCodes.Person
          ] as NormalizedV2Response,
          contactInformationData: (
            (normalizedResponse.entities[EdsEntityCodes.Party] as NormalizedV2Response).entities[
              EdsEntityCodes.Person
            ] as NormalizedV2Response
          ).entities[EdsEntityCodes.ContactInformation] as NormalizedV2Response,
          entityAddressList,
          entityAddressHistory: null,
        }),
      );
    }
  } catch (error) {
    yield put(getEntityDetailsError());
    console.error(error);
  }
}

function* getEDSProducerDetailsSaga(
  action: PayloadAction<{
    id: string;
    producerEntityType: ProducerEntityType;
  }>,
) {
  try {
    yield put(startLoadingEntityDetails());

    const { id, producerEntityType } = action.payload;

    const { data } = yield call(
      HttpService.get,
      `v2/${producerEntityType}/${id}?includeEntities=Addresses.AddressTypes.AddressType,ContactInformation,Contacts.Party.Person.ContactInformation,Contacts.Party.Business.ContactInformation${producerEntityType === EdsEntityCodes.Producer ? ',ProducerGroup' : ''}`,
      {},
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );

    const normalizedResponse: NormalizedV2Response = yield call(EdsAdapterService.normalize, data, {
      includeId: true,
      section: EdsDefinitionSections.SidePanel,
    });

    const addresses = normalizedResponse.entities[EdsEntityCodes.Addresses] as NormalizedV2Response[];
    const entityAddressList =
      addresses?.map((item) => ({ ...item, addressEditId: item.id }) as NormalizedV2Response) ?? [];

    const contactInformation = normalizedResponse.entities[EdsEntityCodes.ContactInformation] as NormalizedV2Response;

    if (data) {
      EdsDebugService.logResponse(EdsEntityCodes.Producer, {
        rawResponse: data,
        normalizedResponse,
        includedEntitiesRecord: normalizedResponse.entities,
      });

      yield put(
        getEntityDetailsSuccess({
          entityDetailsData: normalizedResponse,
          personOrBusinessData: null as unknown as NormalizedV2Response,
          contactInformationData: contactInformation,
          entityAddressList,
          entityAddressHistory: null,
        }),
      );
    }
  } catch (error) {
    yield put(getEntityDetailsError());
    console.error(error);
  }
}

function* getEntityAddressHistorySaga({
  payload: { entityTypeId, entityType, entity },
}: PayloadAction<GetEntityAddressHistoryPayload>) {
  try {
    const filterPropertyName = getFilterPropertyName({ entityType, entity });
    const { data } = yield call(
      HttpService.post,
      `/filter/${EdsEntityCodes.AddressHistory}?page=1&perPage=10000&includes=AddressType`,
      [
        {
          propertyName: filterPropertyName,
          values: [`${entityTypeId}`],
        },
      ],
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );

    const normalizedResponse: NormalizedV2Response[] = yield call(EdsAdapterService.normalize, data.listings, {
      section: EdsDefinitionSections.SidePanel,
    });

    yield put(getEntityAddressHistorySuccess({ entityTypeId, entityType, entity, data: normalizedResponse }));
  } catch (error) {
    console.error(error);
    yield put(getEntityAddressHistoryError());
  }
}

function* deleteEntityPaymentInformationSaga({
  payload: { paymentMethodId },
}: PayloadAction<{ paymentMethodId: string | number }>) {
  try {
    yield call(HttpService.delete, `/${EdsEntityCodes.Payment}/payment-method/${paymentMethodId}`);

    EventService.dispatch(new PaymentFormEvent.PaymentMethodSuccess({}));

    SnacksService.add({
      type: SnackType.Success,
      title: `Payment method was successfully deleted!`,
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  } catch (error) {
    console.error(error);
  }
}

export function* entityDetailsWatcher() {
  yield takeLatest([getInsuredDetails.type], getEDSInsuredDetailsSaga);
  yield takeLatest([getBeneficiaryDetails.type], getEDSBeneficiaryDetailsSaga);
  yield takeLatest([getProducerDetails.type], getEDSProducerDetailsSaga);
  yield takeLatest([refreshContactList.type], refreshContactListSaga);
  yield takeLatest([getContactList.type, incrementContactListPage.type], getContactListSaga);
  yield takeLatest(getEntityAddressHistory.type, getEntityAddressHistorySaga);
  yield takeLatest(deleteEntityPaymentInformation.type, deleteEntityPaymentInformationSaga);
}
